import {
    BuildOutlined,
    CheckCircleRounded,
    EmailRounded,
    ErrorOutlineRounded,
    ExpandMoreRounded,
    FormatQuoteRounded,
    HistoryRounded,
    MemoryRounded,
    RemoveCircleOutlineRounded,
    SmartToyOutlined,
} from "@mui/icons-material";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Card,
    CardActionArea,
    CardContent,
    Chip,
    Divider,
    Grid,
    Paper,
    styled,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
    useTheme,
} from "@mui/material";
import ConfirmationDialog from "components/UI/AcxConfirmationDialog";
import AcxEmailChipInput, {
    AcxEmailChipInputHandle,
    EmailSuggestion,
} from "components/UI/AcxEmailChipInput";
import React, { useRef, useState } from "react";
import {
    KboAgentInvocation,
    KboToolCall,
    KboAnalysisFinding,
    KboCohortRefinementResult,
    KboCohortDefinition,
    KboFlowRunOutput,
    KboPhaseRecord,
    KboPriorRun,
    KboReport,
    KboResearchQuestion,
    KboResolvedCohort,
} from "./KboFlowRunDebug";
import {
    Field,
    formatDate,
    formatDuration,
    formatNumber,
    JsonCard,
    LabeledChip,
    renderable,
    ScoreBar,
    SectionHeading,
    SectionStack,
    shortId,
    StatTile,
} from "./KboFlowDebugUI";
import { KboFlowAnalysisResultsSection } from "./KboFlowAnalysisResultsSection";
import { estimateInvocationsCost, formatUsd } from "./kboModelPricing";

/** Prior runs sorted newest-first by completion time. */
function sortedPriorRuns(output: KboFlowRunOutput): KboPriorRun[] {
    const priorRuns =
        output.assembledContext?.generationHistory?.priorRuns ?? [];
    return [...priorRuns].sort((a, b) => {
        const aTime = a.runCompletedAtUtc
            ? new Date(a.runCompletedAtUtc).getTime()
            : 0;
        const bTime = b.runCompletedAtUtc
            ? new Date(b.runCompletedAtUtc).getTime()
            : 0;
        return bTime - aTime;
    });
}

/* ----------------------------- phase timeline ----------------------------- */

function phaseStatusVisual(status?: string) {
    switch ((status ?? "").toLowerCase()) {
        case "completed":
            return { color: "#2e7d32", icon: <CheckCircleRounded /> };
        case "failed":
            return { color: "#d32f2f", icon: <ErrorOutlineRounded /> };
        case "skipped":
            return { color: "#9e9e9e", icon: <RemoveCircleOutlineRounded /> };
        default:
            return { color: "#9e9e9e", icon: <RemoveCircleOutlineRounded /> };
    }
}

function PhaseTimeline({ phases }: { phases: KboPhaseRecord[] }) {
    const theme = useTheme();
    if (phases.length === 0) return null;
    return (
        <Box
            sx={{
                alignItems: "stretch",
                display: "flex",
                flexWrap: "wrap",
                gap: 0,
                width: "100%",
            }}
        >
            {phases.map((phase, index) => {
                const visual = phaseStatusVisual(phase.status);
                const isAgent =
                    (phase.executionModel ?? "").toLowerCase() === "agent";
                return (
                    <React.Fragment key={`${phase.phase}-${index}`}>
                        <Tooltip
                            arrow
                            title={
                                <Box sx={{ p: 0.5 }}>
                                    <Typography variant="caption">
                                        In: {phase.inputSummary || "—"}
                                    </Typography>
                                    <br />
                                    <Typography variant="caption">
                                        Out: {phase.outputSummary || "—"}
                                    </Typography>
                                    <br />
                                    <Typography variant="caption">
                                        {formatDuration(
                                            phase.startedAtUtc,
                                            phase.completedAtUtc,
                                        )}
                                    </Typography>
                                </Box>
                            }
                        >
                            <Box
                                sx={{
                                    alignItems: "center",
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 0.5,
                                    minWidth: 120,
                                    px: 1,
                                }}
                            >
                                <Box sx={{ color: visual.color }}>
                                    {visual.icon}
                                </Box>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        fontWeight: 600,
                                        textAlign: "center",
                                    }}
                                >
                                    {phase.phase ?? "—"}
                                </Typography>
                                <Chip
                                    size="small"
                                    variant="outlined"
                                    icon={
                                        isAgent ? (
                                            <SmartToyOutlined />
                                        ) : (
                                            <MemoryRounded />
                                        )
                                    }
                                    label={phase.executionModel ?? "—"}
                                />
                                {(phase.invocations?.length ?? 0) > 0 && (
                                    <Chip
                                        size="small"
                                        color="info"
                                        variant="outlined"
                                        label={`${
                                            phase.invocations!.length
                                        } LLM ${
                                            phase.invocations!.length === 1
                                                ? "call"
                                                : "calls"
                                        }`}
                                    />
                                )}
                            </Box>
                        </Tooltip>
                        {index < phases.length - 1 && (
                            <Box
                                sx={{
                                    alignSelf: "center",
                                    borderTop: `2px dashed ${theme.palette.lightgrayBorder.main}`,
                                    flex: "1 1 16px",
                                    minWidth: 16,
                                }}
                            />
                        )}
                    </React.Fragment>
                );
            })}
        </Box>
    );
}

/* -------------------------------- overview -------------------------------- */

export function OverviewSection({ output }: { output: KboFlowRunOutput }) {
    const request = output.request;
    const params = request?.parameters;
    const phases = output.phaseRecords ?? [];
    const accepted = (output.validatedQuestions ?? []).filter(
        (q) => q.validation?.accepted,
    ).length;
    const cachedInputTokens = phases.reduce(
        (sum, phase) =>
            sum +
            (phase.invocations ?? []).reduce(
                (phaseSum, invocation) =>
                    phaseSum + (invocation.cachedInputTokens ?? 0),
                0,
            ),
        0,
    );
    const cost = estimateInvocationsCost(
        phases.flatMap((phase) => phase.invocations ?? []),
    );
    const isRefinement = output.refinementResults !== undefined;
    const isMultiLevelAnalysis = output.analysisResults !== undefined;
    return (
        <SectionStack>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                <StatTile
                    label="Mode"
                    value={
                        isRefinement
                            ? "Cohort Refinement"
                            : request?.mode ?? "Standard Analysis"
                    }
                    accent="primary.main"
                />
                <StatTile
                    label="Duration"
                    value={formatDuration(
                        output.startedAtUtc,
                        output.completedAtUtc,
                    )}
                />
                <StatTile
                    label="Questions"
                    value={output.generatedQuestions?.length ?? 0}
                    hint="Generated questions"
                />
                <StatTile
                    label="Validated"
                    value={`${accepted}/${
                        output.validatedQuestions?.length ?? 0
                    }`}
                    hint="Accepted / validated"
                />
                <StatTile
                    label="Pursued"
                    value={output.pursuedQuestions?.length ?? 0}
                />
                <StatTile
                    label={
                        isRefinement
                            ? "Refinements"
                            : isMultiLevelAnalysis
                            ? "Analyses"
                            : "Findings"
                    }
                    value={
                        isRefinement
                            ? output.refinementResults?.length ?? 0
                            : isMultiLevelAnalysis
                            ? output.analysisResults?.length ?? 0
                            : output.analysisFindings?.length ?? 0
                    }
                />
                <StatTile
                    label="Cached Input"
                    value={formatNumber(cachedInputTokens)}
                    accent={cachedInputTokens > 0 ? "success.main" : undefined}
                    hint="Prompt-cache input tokens recorded across all LLM invocations."
                />
                <StatTile
                    label="Est. Cost"
                    value={
                        cost.hasPricedTokens ? formatUsd(cost.totalUsd) : "—"
                    }
                    accent={cost.hasPricedTokens ? "primary.main" : undefined}
                    hint={
                        cost.unpricedModels.length
                            ? `Estimated from per-model token pricing. Excludes unpriced models: ${cost.unpricedModels.join(
                                  ", ",
                              )}.`
                            : "Estimated from per-model token pricing across all LLM invocations."
                    }
                />
            </Box>

            {phases.length > 0 && (
                <Paper variant="outlined" sx={{ p: 2 }}>
                    <SectionHeading>Phase Pipeline</SectionHeading>
                    <Box sx={{ mt: 2 }}>
                        <PhaseTimeline phases={phases} />
                    </Box>
                </Paper>
            )}

            <Paper variant="outlined" sx={{ p: 2 }}>
                <SectionHeading>Request</SectionHeading>
                <Box sx={{ mt: 1 }}>
                    <Field label="Run ID" value={output.runId} />
                    <Field label="Tenant ID" value={request?.tenantId} />
                    <Field
                        label="Subject User"
                        value={request?.subjectUserId}
                    />
                    <Field
                        label="Date Range"
                        value={`${formatDate(
                            request?.startDateUtc,
                        )}  →  ${formatDate(request?.endDateUtc)}`}
                    />
                    <Field
                        label="Target Questions"
                        value={params?.targetQuestionCount}
                    />
                    <Field
                        label="Max To Pursue"
                        value={params?.maxQuestionsToPursue}
                    />
                    <Divider sx={{ my: 1 }} />
                    <Field
                        label="Started"
                        value={formatDate(output.startedAtUtc)}
                    />
                    <Field
                        label="Completed"
                        value={formatDate(output.completedAtUtc)}
                    />
                </Box>
            </Paper>

            {output.committedHistory && (
                <JsonCard
                    title="Committed History"
                    data={output.committedHistory}
                />
            )}
        </SectionStack>
    );
}

/* --------------------------------- context -------------------------------- */

function availabilityColor(availability?: string) {
    switch ((availability ?? "").toLowerCase()) {
        case "available":
            return "success";
        case "empty":
            return "warning";
        case "restricted":
            return "info";
        case "unavailable":
            return "error";
        default:
            return "default";
    }
}

function PriorRunsPanel({ output }: { output: KboFlowRunOutput }) {
    const priorRuns = sortedPriorRuns(output);
    if (priorRuns.length === 0) return null;
    return (
        <Paper variant="outlined" sx={{ p: 2 }}>
            <SectionHeading count={priorRuns.length}>
                <Box sx={{ alignItems: "center", display: "flex", gap: 0.5 }}>
                    <HistoryRounded fontSize="small" /> Prior Runs
                </Box>
            </SectionHeading>
            <Table size="small" sx={{ mt: 1 }}>
                <TableHead>
                    <TableRow>
                        <TableCell>Completed</TableCell>
                        <TableCell>Run</TableCell>
                        <TableCell align="right">Questions</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {priorRuns.map((prior, index) => (
                        <TableRow key={prior.runId ?? index}>
                            <TableCell>
                                {formatDate(prior.runCompletedAtUtc)}
                            </TableCell>
                            <TableCell>{shortId(prior.runId)}</TableCell>
                            <TableCell align="right">
                                {prior.questions?.length ?? 0}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </Paper>
    );
}

export function ContextSection({ output }: { output: KboFlowRunOutput }) {
    const context = output.assembledContext;
    if (!context) {
        return <Typography variant="body2">No context assembled.</Typography>;
    }
    const sections = context.sectionStatuses ?? [];
    const sources = context.sources ?? [];
    const subContexts: Array<{ title: string; data: unknown }> = [
        { title: "Identity", data: context.identity },
        { title: "Tenant Profile", data: context.tenantProfile },
        { title: "User Profile", data: context.userProfile },
        { title: "Permissions", data: context.permissions },
        { title: "Classifier Catalog", data: context.classifierCatalog },
        { title: "Signal Snapshot", data: context.signalSnapshot },
        { title: "Hierarchies", data: context.hierarchies },
        { title: "Generation History", data: context.generationHistory },
    ];
    return (
        <SectionStack>
            {sections.length > 0 && (
                <Paper variant="outlined" sx={{ p: 2 }}>
                    <SectionHeading count={sections.length}>
                        Section Statuses
                    </SectionHeading>
                    <Box
                        sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 1,
                            mt: 1.5,
                        }}
                    >
                        {sections.map((section, index) => (
                            <LabeledChip
                                key={`${section.section}-${index}`}
                                color={availabilityColor(section.availability)}
                                variant="filled"
                                label={`${section.section ?? "—"} · ${
                                    section.availability ?? "—"
                                } (${section.rowCount ?? 0})`}
                            />
                        ))}
                    </Box>
                </Paper>
            )}

            {sources.length > 0 && (
                <Paper variant="outlined" sx={{ p: 2 }}>
                    <SectionHeading count={sources.length}>
                        Sources
                    </SectionHeading>
                    <Table size="small" sx={{ mt: 1 }}>
                        <TableHead>
                            <TableRow>
                                <TableCell>Section</TableCell>
                                <TableCell>Provider</TableCell>
                                <TableCell>Operation</TableCell>
                                <TableCell align="right">Rows</TableCell>
                                <TableCell align="center">Mock</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sources.map((source, index) => (
                                <TableRow key={`${source.provider}-${index}`}>
                                    <TableCell>
                                        {source.section ?? "—"}
                                    </TableCell>
                                    <TableCell>
                                        {source.provider ?? "—"}
                                    </TableCell>
                                    <TableCell>
                                        {source.operation ?? "—"}
                                    </TableCell>
                                    <TableCell align="right">
                                        {source.rowCount ?? "—"}
                                    </TableCell>
                                    <TableCell align="center">
                                        {source.isMock ? (
                                            <Chip
                                                size="small"
                                                color="warning"
                                                label="mock"
                                            />
                                        ) : (
                                            "—"
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Paper>
            )}

            {context.snapshotText && (
                <Paper variant="outlined" sx={{ p: 2 }}>
                    <SectionHeading>Snapshot Text</SectionHeading>
                    <Box
                        component="pre"
                        sx={{
                            backgroundColor: "gray.100",
                            borderRadius: 1,
                            fontFamily: "monospace",
                            fontSize: "0.78em",
                            maxHeight: 360,
                            mt: 1,
                            overflow: "auto",
                            p: 1.5,
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                        }}
                    >
                        {context.snapshotText}
                    </Box>
                </Paper>
            )}

            <PriorRunsPanel output={output} />

            <Box>
                <SectionHeading>Context Sources (raw)</SectionHeading>
                <Grid container spacing={2} sx={{ mt: 0 }}>
                    {subContexts.map((item) => (
                        <Grid item xs={12} md={6} key={item.title}>
                            <JsonCard title={item.title} data={item.data} />
                        </Grid>
                    ))}
                </Grid>
            </Box>
        </SectionStack>
    );
}

/* -------------------------------- questions ------------------------------- */

function QuestionCard({ question }: { question: KboResearchQuestion }) {
    const validation = question.validation;
    return (
        <Card variant="outlined">
            <CardContent>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {question.text ?? "—"}
                </Typography>
                {question.rationale && (
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                    >
                        {question.rationale}
                    </Typography>
                )}
                {question.rejectionReason && (
                    <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
                        Rejected: {question.rejectionReason}
                    </Typography>
                )}
                {validation && (
                    <Box
                        sx={{
                            alignItems: "center",
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 0.75,
                            mt: 1,
                        }}
                    >
                        <Chip
                            size="small"
                            color={validation.accepted ? "success" : "default"}
                            label={
                                validation.accepted ? "Accepted" : "Rejected"
                            }
                        />
                        {validation.relationship && (
                            <Chip
                                size="small"
                                variant="outlined"
                                label={validation.relationship}
                            />
                        )}
                        {validation.alignment?.score !== undefined && (
                            <Chip
                                size="small"
                                variant="outlined"
                                label={`align ${validation.alignment.score}/3`}
                            />
                        )}
                        {validation.actionability?.score !== undefined && (
                            <Chip
                                size="small"
                                variant="outlined"
                                label={`action ${validation.actionability.score}/3`}
                            />
                        )}
                    </Box>
                )}
            </CardContent>
        </Card>
    );
}

export function QuestionsSection({ output }: { output: KboFlowRunOutput }) {
    const lists: Record<string, KboResearchQuestion[]> = {
        generated: output.generatedQuestions ?? [],
        validated: output.validatedQuestions ?? [],
        pursued: output.pursuedQuestions ?? [],
    };
    const [view, setView] = useState<keyof typeof lists>("generated");
    const questions = lists[view];
    return (
        <SectionStack>
            <ToggleButtonGroup
                size="small"
                exclusive
                value={view}
                onChange={(_event, next) => next && setView(next)}
            >
                <ToggleButton value="generated">
                    Generated ({lists.generated.length})
                </ToggleButton>
                <ToggleButton value="validated">
                    Validated ({lists.validated.length})
                </ToggleButton>
                <ToggleButton value="pursued">
                    Pursued ({lists.pursued.length})
                </ToggleButton>
            </ToggleButtonGroup>
            {questions.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                    None.
                </Typography>
            ) : (
                <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
                >
                    {questions.map((question, index) => (
                        <QuestionCard
                            key={question.id ?? index}
                            question={question}
                        />
                    ))}
                </Box>
            )}
        </SectionStack>
    );
}

/* ------------------------------- validation ------------------------------- */

export function ValidationSection({ output }: { output: KboFlowRunOutput }) {
    const questions = [
        ...(output.pursuedQuestions ?? []),
        ...(output.validatedQuestions ?? []),
    ].filter(
        (question, index, all) =>
            all.findIndex((other) => other.id === question.id) === index,
    );
    const validated = questions.filter((question) => !!question.validation);
    if (validated.length === 0) {
        return <Typography variant="body2">No validation details.</Typography>;
    }
    return (
        <SectionStack>
            {validated.map((question, index) => {
                const validation = question.validation!;
                return (
                    <Card variant="outlined" key={question.id ?? index}>
                        <CardContent>
                            <Box
                                sx={{
                                    alignItems: "center",
                                    display: "flex",
                                    gap: 1,
                                    mb: 1,
                                }}
                            >
                                <Chip
                                    size="small"
                                    color={
                                        validation.accepted
                                            ? "success"
                                            : "default"
                                    }
                                    label={
                                        validation.accepted
                                            ? "Accepted"
                                            : "Rejected"
                                    }
                                />
                                <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600 }}
                                >
                                    {question.text ?? "—"}
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 3,
                                    mb: 1,
                                }}
                            >
                                <ScoreBar
                                    label="Alignment"
                                    score={validation.alignment?.score}
                                />
                                <ScoreBar
                                    label="Actionability"
                                    score={validation.actionability?.score}
                                />
                            </Box>
                            <Box
                                sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 0.75,
                                    mb: 1,
                                }}
                            >
                                {validation.relationship && (
                                    <LabeledChip
                                        label={`relationship: ${validation.relationship}`}
                                    />
                                )}
                                {validation.feasibility && (
                                    <LabeledChip
                                        color={
                                            validation.feasibility.passed
                                                ? "success"
                                                : "error"
                                        }
                                        label={`feasibility: ${
                                            validation.feasibility.passed
                                                ? "pass"
                                                : "fail"
                                        }`}
                                    />
                                )}
                                {validation.specificity && (
                                    <LabeledChip
                                        color={
                                            validation.specificity.passed
                                                ? "success"
                                                : "error"
                                        }
                                        label={`specificity: ${
                                            validation.specificity.passed
                                                ? "pass"
                                                : "fail"
                                        }`}
                                    />
                                )}
                                {validation.estimatedCohortShare !==
                                    undefined && (
                                    <LabeledChip
                                        color="info"
                                        label={`cohort: ${(
                                            validation.estimatedCohortShare *
                                            100
                                        ).toFixed(2)}%`}
                                    />
                                )}
                            </Box>
                            <Field label="Reason" value={validation.reason} />
                            <Field label="Scope" value={validation.scope} />
                            <Field label="Target" value={validation.target} />
                            <Field label="Lever" value={validation.lever} />
                            {validation.alignment?.reason && (
                                <Field
                                    label="Alignment note"
                                    value={validation.alignment.reason}
                                />
                            )}
                            {validation.actionability?.reason && (
                                <Field
                                    label="Actionability note"
                                    value={validation.actionability.reason}
                                />
                            )}
                        </CardContent>
                    </Card>
                );
            })}
        </SectionStack>
    );
}

/* -------------------------------- findings -------------------------------- */

function hasItems<T>(items?: T[] | null): items is T[] {
    return Array.isArray(items) && items.length > 0;
}

function CohortDefinitionPanel({
    cohort,
    title,
}: {
    cohort?: KboCohortDefinition;
    title: string;
}) {
    if (!cohort) return null;
    return (
        <Paper variant="outlined" sx={{ p: 1.5 }}>
            <Box
                sx={{
                    alignItems: "center",
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 0.75,
                    mb: 1,
                }}
            >
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {title}
                </Typography>
                <Chip
                    size="small"
                    color="primary"
                    variant="outlined"
                    label={cohort.role ?? "role —"}
                />
                {cohort.name && (
                    <Chip size="small" variant="outlined" label={cohort.name} />
                )}
            </Box>
            <Field
                label="Date Range"
                value={`${formatDate(cohort.startDateUtc)}  →  ${formatDate(
                    cohort.endDateUtc,
                )}`}
            />
            <Field label="Rationale" value={cohort.rationale} />

            {hasItems(cohort.filters) && (
                <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                        Filters
                    </Typography>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.75,
                            mt: 0.5,
                        }}
                    >
                        {cohort.filters.map((filter, index) => (
                            <Box key={index}>
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexWrap: "wrap",
                                        gap: 0.5,
                                    }}
                                >
                                    <Chip
                                        size="small"
                                        variant="outlined"
                                        label={filter.alias ?? "alias —"}
                                    />
                                    <Chip
                                        size="small"
                                        color="info"
                                        variant="outlined"
                                        label={`${filter.operator ?? "op"} ${
                                            filter.value === undefined
                                                ? "—"
                                                : String(filter.value)
                                        }`}
                                    />
                                </Box>
                                {filter.reason && (
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        {filter.reason}
                                    </Typography>
                                )}
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}

            {hasItems(cohort.dimensions) && (
                <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                        Dimensions
                    </Typography>
                    <Box
                        sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 0.5,
                            mt: 0.5,
                        }}
                    >
                        {cohort.dimensions.map((dimension, index) => (
                            <Tooltip
                                key={index}
                                title={dimension.reason ?? ""}
                                arrow
                            >
                                <Chip
                                    size="small"
                                    variant="outlined"
                                    label={
                                        dimension.outputAlias
                                            ? `${dimension.alias} → ${dimension.outputAlias}`
                                            : dimension.alias ?? "—"
                                    }
                                />
                            </Tooltip>
                        ))}
                    </Box>
                </Box>
            )}

            {hasItems(cohort.measures) && (
                <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                        Measures
                    </Typography>
                    <Box
                        sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 0.5,
                            mt: 0.5,
                        }}
                    >
                        {cohort.measures.map((measure, index) => (
                            <Tooltip
                                key={index}
                                title={measure.reason ?? ""}
                                arrow
                            >
                                <Chip
                                    size="small"
                                    color="secondary"
                                    variant="outlined"
                                    label={`${measure.aggregate ?? "agg"}(${
                                        measure.alias ?? "—"
                                    })`}
                                />
                            </Tooltip>
                        ))}
                    </Box>
                </Box>
            )}
        </Paper>
    );
}

function ResolvedCohortPanel({ cohort }: { cohort?: KboResolvedCohort }) {
    if (!cohort) return null;
    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Box
                sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 0.75,
                }}
            >
                <Chip
                    size="small"
                    color={cohort.meetsMinimum ? "success" : "warning"}
                    label={
                        cohort.meetsMinimum ? "minimum met" : "below minimum"
                    }
                />
                <Chip
                    size="small"
                    variant="outlined"
                    label={`${formatNumber(
                        cohort.matchingConversationCount,
                    )} matching conversations`}
                />
                <Chip
                    size="small"
                    variant="outlined"
                    label={`${formatNumber(
                        cohort.relaxationCount,
                    )} relaxations`}
                />
            </Box>
            <CohortDefinitionPanel
                title="Selected Cohort"
                cohort={cohort.cohort}
            />
        </Box>
    );
}

function AnalysisArtifactGrid({ finding }: { finding: KboAnalysisFinding }) {
    const artifacts: Array<{ title: string; data?: unknown[] }> = [
        { title: "Macro Queries", data: finding.macroQueries },
        { title: "Macro Facts", data: finding.macroFacts },
        { title: "Segments", data: finding.segments },
        { title: "Conversation Evidence", data: finding.conversationEvidence },
        { title: "Pattern Findings", data: finding.patternFindings },
        { title: "Conversation IDs", data: finding.conversationIds },
    ];
    const visible = artifacts.filter((item) => hasItems(item.data));
    if (visible.length === 0 && !hasItems(finding.limitations)) return null;
    return (
        <Grid container spacing={1.5} sx={{ mt: 1 }}>
            {visible.map((artifact) => (
                <Grid item xs={12} md={6} key={artifact.title}>
                    <JsonCard title={artifact.title} data={artifact.data} />
                </Grid>
            ))}
            {hasItems(finding.limitations) && (
                <Grid item xs={12} md={6}>
                    <Paper variant="outlined" sx={{ p: 1.5 }}>
                        <SectionHeading count={finding.limitations.length}>
                            Limitations
                        </SectionHeading>
                        <Box
                            component="ul"
                            sx={{ mb: 0, mt: 1, pl: 3, "& li": { mb: 0.5 } }}
                        >
                            {finding.limitations.map((limitation, index) => (
                                <li key={index}>
                                    <Typography variant="body2">
                                        {renderable(limitation)}
                                    </Typography>
                                </li>
                            ))}
                        </Box>
                    </Paper>
                </Grid>
            )}
        </Grid>
    );
}

function RefinementResultCard({
    result,
    index,
}: {
    result: KboCohortRefinementResult;
    index: number;
}) {
    const refinement = result.refinement;
    const passes = refinement?.passes ?? [];
    return (
        <Card variant="outlined">
            <CardContent>
                <Box
                    sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 0.75,
                        mb: 1,
                    }}
                >
                    <Chip
                        size="small"
                        color="primary"
                        label={`${passes.length} refinement pass${
                            passes.length === 1 ? "" : "es"
                        }`}
                    />
                    <Chip
                        size="small"
                        variant="outlined"
                        label={`${
                            refinement?.reviewedConversationCount ?? 0
                        } conversations reviewed`}
                    />
                </Box>
                <Field
                    label="Question"
                    value={
                        refinement?.question?.text ??
                        result.questionId ??
                        `Refinement ${index + 1}`
                    }
                />
                <CohortDefinitionPanel
                    title="Proposed starting cohort"
                    cohort={result.proposedCohort}
                />

                <SectionStack sx={{ mt: 1.5 }}>
                    {passes.map((pass, passIndex) => (
                        <Paper
                            variant="outlined"
                            sx={{ overflow: "hidden" }}
                            key={passIndex}
                        >
                            <Box
                                sx={{
                                    alignItems: "center",
                                    backgroundColor: "gray.100",
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 0.75,
                                    px: 1.5,
                                    py: 1,
                                }}
                            >
                                <Typography
                                    variant="subtitle2"
                                    sx={{ fontWeight: 700 }}
                                >
                                    Pass {passIndex + 1}
                                </Typography>
                                <Chip
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                    label={
                                        pass.selectedDimension?.alias ??
                                        "dimension —"
                                    }
                                />
                                {pass.selectedRow?.value && (
                                    <Chip
                                        size="small"
                                        variant="outlined"
                                        label={`selected: ${pass.selectedRow.value}`}
                                    />
                                )}
                                <Chip
                                    size="small"
                                    variant="outlined"
                                    label={
                                        pass.shouldContinue
                                            ? "continue"
                                            : "stop"
                                    }
                                />
                            </Box>
                            <Box sx={{ p: 1.5 }}>
                                <Field
                                    label="Dimension reasoning"
                                    value={pass.selectedDimension?.reasoning}
                                />
                                <Field
                                    label="Hypothesis"
                                    value={pass.hypothesis}
                                />
                                <Field
                                    label="Decision"
                                    value={pass.reasoning}
                                />

                                {(pass.distribution?.length ?? 0) > 0 && (
                                    <Table size="small" sx={{ my: 1 }}>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Value</TableCell>
                                                <TableCell align="right">
                                                    Conversations
                                                </TableCell>
                                                <TableCell align="right">
                                                    Share
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {pass.distribution?.map(
                                                (row, rowIndex) => (
                                                    <TableRow key={rowIndex}>
                                                        <TableCell>
                                                            {renderable(
                                                                row.value,
                                                            )}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            {formatNumber(
                                                                row.count,
                                                            )}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            {typeof row.share ===
                                                            "number"
                                                                ? `${(
                                                                      row.share *
                                                                      100
                                                                  ).toFixed(
                                                                      1,
                                                                  )}%`
                                                                : "—"}
                                                        </TableCell>
                                                    </TableRow>
                                                ),
                                            )}
                                        </TableBody>
                                    </Table>
                                )}

                                <Grid container spacing={1.5}>
                                    <Grid item xs={12} md={6}>
                                        <JsonCard
                                            title="Evidence"
                                            data={pass.evidence}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <JsonCard
                                            title="Refined cohort"
                                            data={pass.refinedCohort}
                                        />
                                    </Grid>
                                </Grid>
                            </Box>
                        </Paper>
                    ))}
                </SectionStack>

                <Box sx={{ mt: 1.5 }}>
                    <JsonCard
                        title="Final cohort"
                        data={refinement?.finalCohort}
                    />
                </Box>
            </CardContent>
        </Card>
    );
}

function RefinementResultsSection({
    results,
}: {
    results: KboCohortRefinementResult[];
}) {
    if (results.length === 0) {
        return <Typography variant="body2">No refinement results.</Typography>;
    }

    return (
        <SectionStack>
            {results.map((result, index) => (
                <RefinementResultCard
                    result={result}
                    index={index}
                    key={result.questionId ?? index}
                />
            ))}
        </SectionStack>
    );
}

export function FindingsSection({ output }: { output: KboFlowRunOutput }) {
    if (output.refinementResults !== undefined) {
        return <RefinementResultsSection results={output.refinementResults} />;
    }

    if (output.analysisResults !== undefined) {
        return (
            <KboFlowAnalysisResultsSection results={output.analysisResults} />
        );
    }

    const findings = output.analysisFindings ?? [];
    if (findings.length === 0) {
        return <Typography variant="body2">No findings.</Typography>;
    }
    const questionById = new Map(
        [
            ...(output.generatedQuestions ?? []),
            ...(output.validatedQuestions ?? []),
            ...(output.pursuedQuestions ?? []),
        ].map((question) => [question.id, question]),
    );
    return (
        <SectionStack>
            {findings.map((finding: KboAnalysisFinding, index) => (
                <Card variant="outlined" key={finding.questionId ?? index}>
                    <CardContent>
                        <Box
                            sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 0.75,
                                mb: 1,
                            }}
                        >
                            <Chip
                                size="small"
                                color="primary"
                                label={`level: ${finding.level ?? "—"}`}
                            />
                            <Chip
                                size="small"
                                variant="outlined"
                                label={`${
                                    finding.cohort?.matchingConversationCount ??
                                    0
                                } conversations`}
                            />
                            {finding.cohort?.cohort?.name && (
                                <Chip
                                    size="small"
                                    variant="outlined"
                                    label={finding.cohort.cohort.name}
                                />
                            )}
                            {finding.conversationIds &&
                                finding.conversationIds.length > 0 && (
                                    <Chip
                                        size="small"
                                        variant="outlined"
                                        label={`${finding.conversationIds.length} ids`}
                                    />
                                )}
                        </Box>
                        <Field
                            label="Question"
                            value={
                                questionById.get(finding.questionId)?.text ??
                                finding.questionId
                            }
                        />
                        <ResolvedCohortPanel cohort={finding.cohort} />

                        {hasItems(finding.candidateCohorts) && (
                            <Box sx={{ mt: 1.5 }}>
                                <SectionHeading
                                    count={finding.candidateCohorts.length}
                                >
                                    Candidate Cohorts
                                </SectionHeading>
                                <Grid container spacing={1.5} sx={{ mt: 0 }}>
                                    {finding.candidateCohorts.map(
                                        (cohort, cohortIndex) => (
                                            <Grid
                                                item
                                                xs={12}
                                                md={6}
                                                key={`${cohort.name}-${cohortIndex}`}
                                            >
                                                <CohortDefinitionPanel
                                                    title={`Candidate ${
                                                        cohortIndex + 1
                                                    }`}
                                                    cohort={cohort}
                                                />
                                            </Grid>
                                        ),
                                    )}
                                </Grid>
                            </Box>
                        )}

                        {finding.patterns && finding.patterns.length > 0 && (
                            <Box sx={{ mb: 1 }}>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                >
                                    Patterns
                                </Typography>
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexWrap: "wrap",
                                        gap: 0.5,
                                        mt: 0.5,
                                    }}
                                >
                                    {finding.patterns.map((pattern, i) => (
                                        <Chip
                                            key={i}
                                            size="small"
                                            variant="outlined"
                                            label={renderable(pattern)}
                                        />
                                    ))}
                                </Box>
                            </Box>
                        )}
                        {finding.toolsUsed && finding.toolsUsed.length > 0 && (
                            <Box sx={{ mb: 1 }}>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                >
                                    Tools Used
                                </Typography>
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexWrap: "wrap",
                                        gap: 0.5,
                                        mt: 0.5,
                                    }}
                                >
                                    {finding.toolsUsed.map((tool, i) => (
                                        <Chip
                                            key={i}
                                            size="small"
                                            color="info"
                                            variant="outlined"
                                            label={renderable(tool)}
                                        />
                                    ))}
                                </Box>
                            </Box>
                        )}
                        {finding.quotes && finding.quotes.length > 0 && (
                            <Box sx={{ mt: 1 }}>
                                {finding.quotes.map((quote, i) => (
                                    <Box
                                        key={i}
                                        sx={{
                                            alignItems: "flex-start",
                                            borderLeft: "3px solid",
                                            borderColor: "primary.light",
                                            display: "flex",
                                            gap: 0.5,
                                            mt: 0.5,
                                            pl: 1,
                                        }}
                                    >
                                        <FormatQuoteRounded
                                            fontSize="small"
                                            color="disabled"
                                        />
                                        <Typography
                                            variant="body2"
                                            sx={{ fontStyle: "italic" }}
                                        >
                                            {quote.conversationId && (
                                                <Chip
                                                    size="small"
                                                    variant="outlined"
                                                    label={shortId(
                                                        quote.conversationId,
                                                    )}
                                                    sx={{ mr: 0.75 }}
                                                />
                                            )}
                                            {quote.text}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        )}
                        <AnalysisArtifactGrid finding={finding} />
                    </CardContent>
                </Card>
            ))}
        </SectionStack>
    );
}

/* --------------------------------- report --------------------------------- */

/** Selectable headline card for one report in a multi-report run. */
function ReportHeadlineCard({
    report,
    index,
    active,
    onSelect,
}: {
    report: KboReport;
    index: number;
    active: boolean;
    onSelect: () => void;
}) {
    return (
        <Card
            variant="outlined"
            sx={{
                borderColor: active ? "primary.main" : "lightgrayBorder.main",
                borderWidth: active ? 2 : 1,
                height: "100%",
            }}
        >
            <CardActionArea
                onClick={onSelect}
                sx={{
                    alignItems: "center",
                    display: "flex",
                    gap: 1,
                    height: "100%",
                    px: 1,
                    py: 0.75,
                }}
            >
                <Chip
                    size="small"
                    color={active ? "primary" : "default"}
                    variant={active ? "filled" : "outlined"}
                    label={index + 1}
                />
                <Tooltip title={report.title ?? `Report ${index + 1}`} arrow>
                    <Typography
                        variant="body2"
                        sx={{
                            fontWeight: active ? 700 : 500,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                        }}
                    >
                        {report.title ?? `Report ${index + 1}`}
                    </Typography>
                </Tooltip>
            </CardActionArea>
        </Card>
    );
}

export function ReportSection({
    output,
    onSendReportEmail,
    emailSubmitting,
    emailSuggestions,
    emailSuggestionsLoading,
}: {
    output: KboFlowRunOutput;
    onSendReportEmail: (
        recipients: string[],
        reportIndex: number,
    ) => Promise<boolean>;
    emailSubmitting: boolean;
    emailSuggestions: EmailSuggestion[];
    emailSuggestionsLoading: boolean;
}) {
    const [recipients, setRecipients] = useState<string[]>([]);
    const [pendingText, setPendingText] = useState("");
    const [emailModalOpen, setEmailModalOpen] = useState(false);
    const [selectedReport, setSelectedReport] = useState(0);
    const emailInputRef = useRef<AcxEmailChipInputHandle>(null);
    const reports = output.reports ?? [];
    if (reports.length === 0) {
        return <Typography variant="body2">No report.</Typography>;
    }
    const activeIndex = selectedReport < reports.length ? selectedReport : 0;
    const report = reports[activeIndex];
    const reportHtml = report.email?.htmlBody;
    const multiple = reports.length > 1;

    const openEmailModal = () => {
        setPendingText("");
        setEmailModalOpen(true);
    };

    const handleSend = async () => {
        // Commit any address still typed in the box so it is included, not dropped.
        const finalRecipients =
            emailInputRef.current?.flushPending() ?? recipients;
        const sent = await onSendReportEmail(finalRecipients, activeIndex);
        if (sent) {
            // Keep the recipient list so the report can be re-sent to the same
            // people without re-entering them.
            setEmailModalOpen(false);
        }
    };

    return (
        <SectionStack>
            {/* Headline picker: every report title is visible at once so switching
                between them is a single click (runs produce ~1–10 reports). */}
            {multiple && (
                <Box>
                    <SectionHeading count={reports.length}>
                        Reports
                    </SectionHeading>
                    <Grid container spacing={1} sx={{ mt: 0 }}>
                        {reports.map((item, index) => (
                            <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                                <ReportHeadlineCard
                                    report={item}
                                    index={index}
                                    active={index === activeIndex}
                                    onSelect={() => setSelectedReport(index)}
                                />
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            )}

            {/* Selected report: title + send action. */}
            <Box
                sx={{
                    alignItems: "center",
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                    justifyContent: "space-between",
                }}
            >
                <Box>
                    {multiple && (
                        <Typography
                            variant="overline"
                            sx={{ color: "text.secondary", letterSpacing: 1 }}
                        >
                            {`Report ${activeIndex + 1} of ${reports.length}`}
                        </Typography>
                    )}
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {report.title ?? "Untitled report"}
                    </Typography>
                </Box>
                {reportHtml && (
                    <Button
                        variant="contained"
                        startIcon={<EmailRounded />}
                        onClick={openEmailModal}
                    >
                        Send email
                    </Button>
                )}
            </Box>

            {/* Rendered report. */}
            {reportHtml ? (
                <Paper variant="outlined" sx={{ overflow: "hidden" }}>
                    <Box
                        component="iframe"
                        title="Generated report HTML"
                        srcDoc={reportHtml}
                        sandbox=""
                        sx={{
                            border: 0,
                            display: "block",
                            height: "70vh",
                            minHeight: 640,
                            width: "100%",
                        }}
                    />
                </Paper>
            ) : (
                <Typography variant="body2" color="text.secondary">
                    This report has no rendered HTML.
                </Typography>
            )}

            <ConfirmationDialog
                isOpen={emailModalOpen}
                title="Send report"
                subTitle="Enter one or more email addresses. Leave empty to send the report to yourself."
                maxWidth="sm"
                fullWidth
                onClose={() => setEmailModalOpen(false)}
                onConfirm={handleSend}
                isLoading={emailSubmitting}
                confirmButtonText={
                    recipients.length || pendingText.trim()
                        ? "Send report"
                        : "Send to me"
                }
                content={
                    <AcxEmailChipInput
                        ref={emailInputRef}
                        values={recipients}
                        setValues={setRecipients}
                        suggestions={emailSuggestions}
                        loading={emailSuggestionsLoading}
                        placeholderText="Add email addresses…"
                        onPendingTextChange={setPendingText}
                    />
                }
            />
        </SectionStack>
    );
}

/* ------------------------------- invocations ------------------------------ */

export function invocationStatusColor(status?: string) {
    switch ((status ?? "").toLowerCase()) {
        case "succeeded":
        case "success":
        case "completed":
            return "success";
        case "failed":
        case "error":
            return "error";
        default:
            return "default";
    }
}

const PromptBlock = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.gray[100],
    borderRadius: theme.shape.borderRadius / 2,
    fontFamily: "monospace",
    fontSize: "0.78em",
    marginTop: theme.spacing(0.5),
    maxHeight: 320,
    overflow: "auto",
    padding: theme.spacing(1.5),
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
}));

export function InvocationRow({
    invocation,
    phase,
}: {
    invocation: KboAgentInvocation;
    phase?: string;
}) {
    const inputTokens = invocation.inputTokens ?? 0;
    const cachedInputTokens = invocation.cachedInputTokens ?? 0;
    const uncachedInputTokens = Math.max(0, inputTokens - cachedInputTokens);
    const tokens =
        invocation.totalTokens ?? inputTokens + (invocation.outputTokens ?? 0);
    const toolCalls = invocation.toolCalls ?? [];
    return (
        <Accordion
            disableGutters
            TransitionProps={{ unmountOnExit: true }}
            sx={{
                "&:before": { display: "none" },
                border: "1px solid",
                borderColor: "lightgrayBorder.main",
                borderRadius: 1,
                overflow: "hidden",
            }}
        >
            <AccordionSummary expandIcon={<ExpandMoreRounded />}>
                <Box
                    sx={{
                        alignItems: "center",
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1,
                        pr: 2,
                        width: "100%",
                    }}
                >
                    {phase && (
                        <Chip size="small" variant="outlined" label={phase} />
                    )}
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {invocation.label ?? "—"}
                    </Typography>
                    <Box sx={{ flex: 1 }} />
                    <Chip
                        size="small"
                        color={invocationStatusColor(invocation.status)}
                        label={invocation.status ?? "—"}
                    />
                    {(invocation.model || invocation.deployment) && (
                        <Chip
                            size="small"
                            variant="outlined"
                            icon={<SmartToyOutlined />}
                            label={
                                invocation.deployment
                                    ? `${invocation.model ?? "?"} · ${
                                          invocation.deployment
                                      }`
                                    : invocation.model
                            }
                        />
                    )}
                    <Chip
                        size="small"
                        variant="outlined"
                        label={`${formatNumber(
                            inputTokens,
                        )} in → ${formatNumber(invocation.outputTokens)} out`}
                    />
                    {cachedInputTokens > 0 && (
                        <Chip
                            size="small"
                            color="success"
                            variant="outlined"
                            label={`${formatNumber(cachedInputTokens)} cached`}
                        />
                    )}
                    <Chip
                        size="small"
                        variant="outlined"
                        label={`${formatNumber(
                            uncachedInputTokens,
                        )} uncached in`}
                    />
                    <Chip
                        size="small"
                        variant="outlined"
                        label={`${formatNumber(tokens)} total`}
                    />
                    <Chip
                        size="small"
                        variant="outlined"
                        label={formatDuration(
                            invocation.startedAtUtc,
                            invocation.completedAtUtc,
                        )}
                    />
                    {toolCalls.length > 0 && (
                        <Chip
                            size="small"
                            color="info"
                            variant="outlined"
                            icon={<BuildOutlined />}
                            label={`${toolCalls.length} tool call${
                                toolCalls.length === 1 ? "" : "s"
                            }`}
                        />
                    )}
                </Box>
            </AccordionSummary>
            <AccordionDetails>
                {invocation.systemPrompt && (
                    <Box sx={{ mb: 1 }}>
                        <Typography variant="subtitle2">
                            System Prompt
                        </Typography>
                        <PromptBlock component="pre">
                            {invocation.systemPrompt}
                        </PromptBlock>
                    </Box>
                )}
                {invocation.userPrompt && (
                    <Box sx={{ mb: 1 }}>
                        <Typography variant="subtitle2">User Prompt</Typography>
                        <PromptBlock component="pre">
                            {invocation.userPrompt}
                        </PromptBlock>
                    </Box>
                )}
                {invocation.response !== undefined &&
                    invocation.response !== null &&
                    invocation.response !== "" && (
                        <Box sx={{ mb: 1 }}>
                            <Typography variant="subtitle2">
                                Response
                            </Typography>
                            <PromptBlock component="pre">
                                {typeof invocation.response === "string"
                                    ? invocation.response
                                    : JSON.stringify(
                                          invocation.response,
                                          null,
                                          2,
                                      )}
                            </PromptBlock>
                        </Box>
                    )}
                {toolCalls.length > 0 && (
                    <Box sx={{ mb: 1 }}>
                        <Typography variant="subtitle2">Tool Calls</Typography>
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 1,
                                mt: 0.5,
                            }}
                        >
                            {toolCalls.map((call, index) => (
                                <ToolCallCard key={index} call={call} />
                            ))}
                        </Box>
                    </Box>
                )}
                {invocation.error && (
                    <Box sx={{ mb: 1 }}>
                        <Typography variant="subtitle2" color="error">
                            Error
                        </Typography>
                        <PromptBlock component="pre">
                            {invocation.error}
                        </PromptBlock>
                    </Box>
                )}
            </AccordionDetails>
        </Accordion>
    );
}

/**
 * Parses a JSON-encoded string field. Returns { value } on success (value may be
 * a string, array, or object), or { raw } when the field is absent or not valid JSON.
 */
function parseJsonField(json?: string): { value?: unknown; raw?: string } {
    if (json === undefined || json === null || json === "") return {};
    try {
        return { value: JSON.parse(json) };
    } catch {
        return { raw: json };
    }
}

/** Renders a decoded tool result/arguments value: strings in a PromptBlock, structured data via JsonCard. */
function ToolCallValue({
    title,
    parsed,
}: {
    title: string;
    parsed: { value?: unknown; raw?: string };
}) {
    if (parsed.raw !== undefined || typeof parsed.value === "string") {
        const text = (parsed.raw ?? parsed.value) as string;
        return (
            <Box>
                <Typography variant="caption" color="text.secondary">
                    {title}
                </Typography>
                <PromptBlock component="pre">{text}</PromptBlock>
            </Box>
        );
    }
    return <JsonCard title={title} data={parsed.value ?? {}} />;
}

function ToolCallCard({ call }: { call: KboToolCall }) {
    const args = parseJsonField(call.argumentsJson);
    const result = parseJsonField(call.resultJson);
    const hasResult =
        call.resultJson !== undefined &&
        call.resultJson !== null &&
        call.resultJson !== "";
    return (
        <Paper
            variant="outlined"
            sx={{ display: "flex", flexDirection: "column", gap: 1, p: 1.5 }}
        >
            <Box
                sx={{
                    alignItems: "center",
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                }}
            >
                <Chip
                    size="small"
                    variant="outlined"
                    icon={<BuildOutlined />}
                    label={call.name ?? "—"}
                />
                {call.status && (
                    <Chip
                        size="small"
                        color={invocationStatusColor(call.status)}
                        label={call.status}
                    />
                )}
                <Box sx={{ flex: 1 }} />
                <Chip
                    size="small"
                    variant="outlined"
                    label={formatDuration(
                        call.startedAtUtc,
                        call.completedAtUtc,
                    )}
                />
            </Box>
            <ToolCallValue title="Arguments" parsed={args} />
            {hasResult ? (
                <ToolCallValue title="Result" parsed={result} />
            ) : (
                <Box>
                    <Typography variant="caption" color="text.secondary">
                        Result
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        No result recorded.
                    </Typography>
                </Box>
            )}
        </Paper>
    );
}

export function InvocationsSection({ output }: { output: KboFlowRunOutput }) {
    const phases = output.phaseRecords ?? [];
    const invocations = phases.flatMap((phase) =>
        (phase.invocations ?? []).map((invocation) => ({
            invocation,
            phase: phase.phase,
        })),
    );

    if (invocations.length === 0) {
        return (
            <Typography variant="body2">
                No agent invocations recorded.
            </Typography>
        );
    }

    const toolCallCount = invocations.reduce(
        (sum, item) => sum + (item.invocation.toolCalls?.length ?? 0),
        0,
    );

    const inputTokens = invocations.reduce(
        (sum, item) => sum + (item.invocation.inputTokens ?? 0),
        0,
    );
    const cachedInputTokens = invocations.reduce(
        (sum, item) => sum + (item.invocation.cachedInputTokens ?? 0),
        0,
    );
    const uncachedInputTokens = Math.max(0, inputTokens - cachedInputTokens);
    const outputTokens = invocations.reduce(
        (sum, item) => sum + (item.invocation.outputTokens ?? 0),
        0,
    );
    const totalTokens = invocations.reduce(
        (sum, item) =>
            sum +
            (item.invocation.totalTokens ??
                (item.invocation.inputTokens ?? 0) +
                    (item.invocation.outputTokens ?? 0)),
        0,
    );
    const totalMs = invocations.reduce((sum, item) => {
        const { startedAtUtc, completedAtUtc } = item.invocation;
        if (!startedAtUtc || !completedAtUtc) return sum;
        const ms =
            new Date(completedAtUtc).getTime() -
            new Date(startedAtUtc).getTime();
        return Number.isNaN(ms) || ms < 0 ? sum : sum + ms;
    }, 0);
    const totalSeconds = totalMs / 1000;
    const totalTime =
        totalSeconds < 60
            ? `${totalSeconds.toFixed(1)}s`
            : `${Math.floor(totalSeconds / 60)}m ${Math.round(
                  totalSeconds % 60,
              )}s`;
    const cacheRate =
        inputTokens > 0
            ? `${((cachedInputTokens / inputTokens) * 100).toFixed(1)}%`
            : "—";
    const cost = estimateInvocationsCost(
        invocations.map((item) => item.invocation),
    );
    const costHint = cost.unpricedModels.length
        ? `Estimated from per-model token pricing. Excludes unpriced models: ${cost.unpricedModels.join(
              ", ",
          )}.`
        : "Estimated from per-model token pricing (cached input billed at the discounted rate).";

    return (
        <SectionStack>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                <StatTile label="Calls" value={invocations.length} />
                <StatTile label="Tool Calls" value={toolCallCount} />
                <StatTile
                    label="Input Tokens"
                    value={formatNumber(inputTokens)}
                />
                <StatTile
                    label="Cached Input"
                    value={formatNumber(cachedInputTokens)}
                    accent="success.main"
                />
                <StatTile
                    label="Uncached Input"
                    value={formatNumber(uncachedInputTokens)}
                />
                <StatTile
                    label="Cache Rate"
                    value={cacheRate}
                    hint="Cached input tokens divided by total input tokens."
                />
                <StatTile
                    label="Output Tokens"
                    value={formatNumber(outputTokens)}
                />
                <StatTile
                    label="Total Tokens"
                    value={formatNumber(totalTokens)}
                />
                <StatTile
                    label="Est. Cost"
                    value={
                        cost.hasPricedTokens ? formatUsd(cost.totalUsd) : "—"
                    }
                    accent={cost.hasPricedTokens ? "primary.main" : undefined}
                    hint={costHint}
                />
                <StatTile label="LLM Time" value={totalTime} />
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {invocations.map((item, index) => (
                    <InvocationRow
                        key={index}
                        invocation={item.invocation}
                        phase={item.phase}
                    />
                ))}
            </Box>
        </SectionStack>
    );
}
